import { useState, useMemo, useCallback } from 'react';
import type { SortDescriptor } from 'react-aria-components';
import { ArrowRight, Columns03, Download01, Edit03 } from '@untitledui/icons';
import { Button as AriaButton } from 'react-aria-components';
import { Table, TableCard } from '@/components/application/table/table';
import { Dropdown } from '@/components/base/dropdown/dropdown';
import { Badge } from '@/components/base/badges/badges';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { ContainerRealtimeIndicator } from './container-realtime-indicator';
import { ContainerEditModal } from './container-edit-modal';
import { exportContainers } from '@/utils/export-containers';
import type { Container } from '@/types/container';
import { useTranslation } from 'react-i18next';
import { useI18n } from '@/hooks/use-i18n';
import { useShipmentUpdate, RISK_LEVEL_TO_SCORE } from '@/hooks/api/use-shipment-update';

interface ContainerSpreadsheetViewProps {
    containers: Container[];
    totalCount?: number;
    onUpdate?: (containerId: string, field: string, value: any) => void;
    onContainerClick?: (containerId: string) => void;
    className?: string;
    /** Current sort field */
    sortBy?: string;
    /** Current sort order */
    sortOrder?: 'asc' | 'desc';
    /** Callback when sort changes */
    onSortChange?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
}

// Editable fields that trigger API calls
const EDITABLE_FIELDS = ['actualDepartureDate', 'riskLevel', 'status', 'destinationPort', 'value'];

// Helper functions for color mapping
const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel) {
        case 'Low': return 'success';
        case 'Medium': return 'warning';
        case 'High': return 'error';
        case 'Critical': return 'error';
        default: return 'gray';
    }
};

const getStatusColor = (status: string) => {
    switch (status) {
        case 'Draft': return 'gray';
        case 'Booked': return 'brand';
        case 'In Transit': return 'brand';
        case 'Arrived': return 'warning';
        case 'Delivered': return 'success';
        case 'On Hold': return 'warning';
        case 'Cancelled': return 'error';
        default: return 'gray';
    }
};

// Format date for display
const formatDate = (date: Date | string | null | undefined): string => {
    if (!date) return '-';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Calculate delay days
const calculateDelayDays = (etd: Date | null | undefined, actualTd: Date | null | undefined): number | null => {
    if (!etd || !actualTd) return null;
    const etdDate = typeof etd === 'string' ? new Date(etd) : etd;
    const actualDate = typeof actualTd === 'string' ? new Date(actualTd) : actualTd;
    const diffTime = actualDate.getTime() - etdDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
};

export const ContainerSpreadsheetView = ({
    containers,
    totalCount,
    onUpdate,
    onContainerClick,
    className,
    sortBy,
    sortOrder = 'asc',
    onSortChange,
}: ContainerSpreadsheetViewProps) => {
    const { t } = useTranslation(['pages:containers', 'common', 'types']);
    const { translateContainerStatus, translateRiskLevel } = useI18n();
    const [showMoreDetails, setShowMoreDetails] = useState(false);
    const [editingCell, setEditingCell] = useState<{ containerId: string; field: string } | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [lastUpdateTime] = useState<Date>(new Date());

    // API mutation for updates
    const { mutate: updateShipment, isPending: isUpdating } = useShipmentUpdate({
        onSuccess: () => {
            setEditingCell(null);
            setIsModalOpen(false);
        },
        onError: (error) => {
            console.error('Failed to update shipment:', error);
            // TODO: Show toast notification
        },
    });

    // Handle sort change from table
    const handleSortChange = useCallback((descriptor: SortDescriptor) => {
        if (onSortChange && descriptor.column) {
            const newOrder = descriptor.direction === 'ascending' ? 'asc' : 'desc';
            onSortChange(String(descriptor.column), newOrder);
        }
    }, [onSortChange]);

    // Build sort descriptor for the table
    const sortDescriptor: SortDescriptor | undefined = sortBy
        ? { column: sortBy, direction: sortOrder === 'asc' ? 'ascending' : 'descending' }
        : undefined;

    const handleStartEdit = useCallback((containerId: string, field: string, e: React.MouseEvent) => {
        e.stopPropagation(); // Don't navigate to detail
        if (!EDITABLE_FIELDS.includes(field)) return;
        setEditingCell({ containerId, field });
        setIsModalOpen(true);
    }, []);

    const handleSave = useCallback((containerId: string, field: string, value: any) => {
        // Map frontend field names to API field names
        const apiFieldMap: Record<string, string> = {
            actualDepartureDate: 'actual_departure_date',
            riskLevel: 'risk_score',
            status: 'status',
            destinationPort: 'destination_port_id',
            value: 'total_value_usd',
        };

        const apiField = apiFieldMap[field] || field;
        let apiValue = value;

        // Convert risk level to score
        if (field === 'riskLevel') {
            apiValue = RISK_LEVEL_TO_SCORE[value.toLowerCase()] || 50;
        }

        // Extract amount from value object
        if (field === 'value' && typeof value === 'object') {
            apiValue = value.amount;
        }

        updateShipment({
            shipmentId: containerId,
            payload: { [apiField]: apiValue },
        });

        // Notify parent
        onUpdate?.(containerId, field, value);
    }, [updateShipment, onUpdate]);

    const handleCloseModal = useCallback(() => {
        setEditingCell(null);
        setIsModalOpen(false);
    }, []);

    // Base columns (always visible)
    const baseColumns = useMemo(() => [
        { id: 'containerNumber', label: t('list.table.headers.containerNumber'), sortable: false },
        { id: 'route', label: t('list.table.headers.route'), sortable: false },
        { id: 'shippingCompany', label: t('list.table.headers.carrier'), sortable: false },
        { id: 'progress', label: t('list.table.headers.progress'), sortable: true, sortKey: 'progress' },
        { id: 'etd', label: t('list.table.headers.etd'), sortable: true, sortKey: 'etd' },
        { id: 'eta', label: t('list.table.headers.eta'), sortable: true, sortKey: 'eta' },
        { id: 'status', label: t('list.table.headers.status'), sortable: false },
        { id: 'riskLevel', label: t('list.table.headers.riskLevel'), sortable: true, sortKey: 'risk' },
        { id: 'documentCount', label: t('list.table.headers.documents'), sortable: true, sortKey: 'documents' },
        { id: 'documentStatusOverall', label: t('list.table.headers.docStatus'), sortable: true, sortKey: 'doc_issues' },
        { id: 'exogenStatusOverall', label: t('list.table.headers.extraDocIssues'), sortable: true, sortKey: 'issues' },
        { id: 'lastUpdate', label: t('list.table.headers.lastUpdate'), sortable: true, sortKey: 'last_update' },
    ], [t]);

    // Extended columns (visible when "Show more details" is active)
    const extendedColumns = useMemo(() => [
        { id: 'bookingNumber', label: t('list.table.headers.bookingNo'), sortable: false },
        { id: 'blNumber', label: t('list.table.headers.blNo'), sortable: false },
        { id: 'value', label: t('list.table.headers.valueUsd'), sortable: false, editable: true },
        { id: 'product', label: t('list.table.headers.product'), sortable: false },
        { id: 'quantity', label: t('list.table.headers.qtyWeight'), sortable: false },
        { id: 'temperature', label: t('list.table.headers.tempC'), sortable: false },
        { id: 'actualDepartureDate', label: t('list.table.headers.actualTd'), sortable: false, editable: true },
        { id: 'delayDays', label: t('list.table.headers.delayDays'), sortable: false },
        { id: 'originPort', label: t('list.table.headers.originPort'), sortable: false },
        { id: 'destinationPort', label: t('list.table.headers.destPort'), sortable: false, editable: true },
        { id: 'paymentTerms', label: t('list.table.headers.paymentTerms'), sortable: false, comingSoon: true },
        { id: 'freightTerms', label: t('list.table.headers.freightTerms'), sortable: false, comingSoon: true },
    ], [t]);

    const columns = useMemo(() =>
        showMoreDetails ? [...baseColumns, ...extendedColumns] : baseColumns,
        [baseColumns, extendedColumns, showMoreDetails]
    );

    // Render cell content based on column
    const renderCell = (container: Container, colId: string, isEditable: boolean) => {
        const editButton = isEditable ? (
            <button
                onClick={(e) => handleStartEdit(container.id, colId, e)}
                className="ml-1 p-0.5 text-tertiary hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity"
                title="Edit"
            >
                <Edit03 className="size-3" />
            </button>
        ) : null;

        switch (colId) {
            case 'containerNumber':
                return <span className="font-medium text-primary">{container.containerNumber || '-'}</span>;

            case 'route':
                return (
                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                        <Badge color="gray" size="sm">{container.route.from || '-'}</Badge>
                        <ArrowRight className="size-3.5 shrink-0 text-tertiary" />
                        <Badge color="gray" size="sm">{container.route.to || '-'}</Badge>
                    </div>
                );

            case 'shippingCompany':
                return <span className="text-sm text-tertiary">{container.shippingCompany ?? '-'}</span>;

            case 'progress':
                return (
                    <div className="flex items-center gap-2">
                        <ProgressBar value={container.progress} min={0} max={100} />
                        <span className="text-sm text-tertiary">{container.progress}%</span>
                    </div>
                );

            case 'etd':
                return <span className="text-sm text-tertiary">{formatDate(container.estimatedDepartureDate)}</span>;

            case 'eta':
                return <span className="text-sm text-tertiary">{formatDate(container.eta)}</span>;

            case 'status':
                return (
                    <div className="flex items-center gap-1 group">
                        <Badge color={getStatusColor(container.status)} size="sm">
                            {translateContainerStatus(container.status)}
                        </Badge>
                        {editButton}
                    </div>
                );

            case 'riskLevel':
                return (
                    <div className="flex items-center gap-1 group">
                        <Badge color={getRiskLevelColor(container.riskLevel)} size="sm">
                            {translateRiskLevel(container.riskLevel)}
                        </Badge>
                        {editButton}
                    </div>
                );

            case 'documentCount':
                return <span className="text-sm text-tertiary">{container.documentCount ?? 0}</span>;

            case 'documentStatusOverall': {
                const docIssueCount = container.docsWithIssues ?? 0;
                const noDocUploaded = container.documentStatusOverall === 'No documents uploaded';
                const allOk = docIssueCount === 0 && !noDocUploaded;
                return (
                    <span className={`text-sm ${allOk ? 'text-success-600' : noDocUploaded ? 'text-tertiary' : 'text-warning-600'}`}>
                        {noDocUploaded
                            ? t('list.spreadsheet.noDocuments')
                            : allOk
                                ? t('list.spreadsheet.allDocsOk')
                                : t('list.spreadsheet.issuesInDocs', { count: docIssueCount })}
                    </span>
                );
            }

            case 'exogenStatusOverall': {
                // Parse the issue count from the backend message like "X general problems"
                const issueMatch = container.exogenStatusOverall?.match(/^(\d+)/);
                const issueCount = issueMatch ? parseInt(issueMatch[1], 10) : 0;
                const allSystemsOk = container.exogenStatusOverall === 'All systems OK' || issueCount === 0;
                return (
                    <span className={`text-sm ${allSystemsOk ? 'text-success-600' : 'text-warning-600'}`}>
                        {allSystemsOk
                            ? t('list.spreadsheet.noIssues')
                            : t('list.spreadsheet.issueCount', { count: issueCount })}
                    </span>
                );
            }

            case 'lastUpdate':
                return (
                    <div className="flex flex-col">
                        <span className="text-sm text-tertiary whitespace-nowrap">
                            {formatDate(container.lastUpdateAt)}
                        </span>
                        <span className="text-xs text-quaternary">
                            {container.lastUpdateType || 'Update'}
                        </span>
                    </div>
                );

            // Extended columns
            case 'bookingNumber':
                return <span className="text-sm text-tertiary font-mono">{container.bookingNumber || '-'}</span>;

            case 'blNumber':
                return <span className="text-sm text-tertiary font-mono">{container.blNumber || '-'}</span>;

            case 'value':
                return (
                    <div className="flex items-center gap-1 group">
                        <span className="text-sm text-tertiary">
                            {container.totalValueUsd
                                ? `$${container.totalValueUsd.toLocaleString()}`
                                : container.value?.amount
                                    ? `${container.value.currency} ${container.value.amount.toLocaleString()}`
                                    : '-'}
                        </span>
                        {editButton}
                    </div>
                );

            case 'product':
                const hsCode = container.hsCodePrimary ? container.hsCodePrimary.substring(0, 6) : '';
                const desc = container.cargoDescription?.substring(0, 20) || '';
                return (
                    <span className="text-sm text-tertiary" title={container.cargoDescription || ''}>
                        {hsCode ? `${hsCode} - ${desc}${desc.length >= 20 ? '...' : ''}` : desc || '-'}
                    </span>
                );

            case 'quantity':
                const qty = container.totalPackages;
                const weight = container.totalWeightKg;
                return (
                    <span className="text-sm text-tertiary">
                        {qty ? `${qty} pkg` : ''}{qty && weight ? ' / ' : ''}{weight ? `${weight.toLocaleString()} kg` : ''}{!qty && !weight ? '-' : ''}
                    </span>
                );

            case 'temperature':
                return (
                    <span className="text-sm text-tertiary">
                        {container.requiresTemperatureControl && container.temperatureSetpointCelsius !== undefined
                            ? `${container.temperatureSetpointCelsius}°C`
                            : '-'}
                    </span>
                );

            case 'actualDepartureDate':
                return (
                    <div className="flex items-center gap-1 group">
                        <span className="text-sm text-tertiary">{formatDate(container.actualDepartureDate)}</span>
                        {editButton}
                    </div>
                );

            case 'delayDays': {
                const delay = calculateDelayDays(container.estimatedDepartureDate, container.actualDepartureDate);
                if (delay === null) return <span className="text-sm text-tertiary">-</span>;
                const color = delay > 0 ? 'text-error-600' : delay < 0 ? 'text-success-600' : 'text-tertiary';
                return <span className={`text-sm ${color}`}>{delay > 0 ? `+${delay}` : delay}</span>;
            }

            case 'originPort':
                return <span className="text-sm text-tertiary">{container.route.from || '-'}</span>;

            case 'destinationPort':
                return (
                    <div className="flex items-center gap-1 group">
                        <span className="text-sm text-tertiary">{container.route.to || '-'}</span>
                        {editButton}
                    </div>
                );

            case 'paymentTerms':
            case 'freightTerms':
                return <span className="text-sm text-quaternary italic">{t('list.spreadsheet.comingSoon')}</span>;

            default:
                return <span className="text-sm text-tertiary">-</span>;
        }
    };

    return (
        <div className={className}>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-primary">
                        {t('list.table.title')} {totalCount !== undefined ? `(${t('list.spreadsheet.ofTotal', { current: containers.length, total: totalCount })})` : `(${containers.length})`}
                    </h3>
                    <ContainerRealtimeIndicator lastUpdateTime={lastUpdateTime} />
                </div>

                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setShowMoreDetails(!showMoreDetails)}
                        className="flex items-center gap-1.5 text-sm font-medium text-tertiary hover:text-primary transition-colors"
                    >
                        <Columns03 className="size-4" />
                        <span>{showMoreDetails ? t('list.spreadsheet.fewerColumns') : t('list.spreadsheet.moreColumns')}</span>
                    </button>

                    <Dropdown.Root>
                        <AriaButton className="flex items-center gap-1.5 text-sm font-medium text-tertiary hover:text-primary transition-colors cursor-pointer">
                            <Download01 className="size-4" />
                            <span>{t('list.spreadsheet.export')}</span>
                        </AriaButton>
                        <Dropdown.Popover>
                            <Dropdown.Menu onAction={async (key) => exportContainers(containers, columns, key as 'csv' | 'xlsx')}>
                                <Dropdown.Item id="xlsx" label={t('list.spreadsheet.excelXlsx')} />
                                <Dropdown.Item id="csv" label={t('list.spreadsheet.csvFile')} />
                            </Dropdown.Menu>
                        </Dropdown.Popover>
                    </Dropdown.Root>
                </div>
            </div>

            <TableCard.Root className="relative max-h-[calc(100vh-14rem)] scrollbar-visible">
                <Table sortDescriptor={sortDescriptor} onSortChange={handleSortChange}>
                    <Table.Header>
                        {columns.map((col, index) => (
                            <Table.Head
                                key={col.id}
                                id={col.sortable ? ('sortKey' in col ? col.sortKey : undefined) : undefined}
                                label={col.label}
                                isRowHeader={index === 0}
                                allowsSorting={col.sortable}
                                className={col.id === 'route' ? 'min-w-[280px]' : undefined}
                            />
                        ))}
                    </Table.Header>
                    <Table.Body>
                        {containers.map((container) => (
                            <Table.Row
                                key={container.id}
                                className="cursor-pointer hover:bg-secondary"
                                onClick={() => onContainerClick?.(container.id)}
                            >
                                {columns.map((col) => {
                                    const isEditable = EDITABLE_FIELDS.includes(col.id);
                                    return (
                                        <Table.Cell key={col.id}>
                                            {renderCell(container, col.id, isEditable)}
                                        </Table.Cell>
                                    );
                                })}
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
                {/* Edge gradients: fade from edge inward (shadow into content) */}
                <div
                    aria-hidden
                    className="pointer-events-none absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-primary to-transparent dark:from-gray-800"
                />
                <div
                    aria-hidden
                    className="pointer-events-none absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-primary to-transparent dark:from-gray-800"
                />
                {/* Corner filler so vertical and horizontal scrollbars don't overlap visually */}
                <div
                    aria-hidden
                    className="pointer-events-none absolute bottom-0 right-0 h-[10px] w-[10px] shrink-0 bg-gray-100 dark:bg-gray-800"
                />
            </TableCard.Root>

            {/* Edit Modal */}
            {editingCell && (() => {
                const container = containers.find(c => c.id === editingCell.containerId);
                if (!container) return null;

                let cellValue: any;
                switch (editingCell.field) {
                    case 'actualDepartureDate':
                        cellValue = container.actualDepartureDate;
                        break;
                    case 'riskLevel':
                        cellValue = container.riskLevel;
                        break;
                    case 'status':
                        cellValue = container.status;
                        break;
                    case 'destinationPort':
                        cellValue = container.route.to;
                        break;
                    case 'value':
                        cellValue = container.value || { amount: container.totalValueUsd || 0, currency: 'USD' };
                        break;
                    default:
                        cellValue = (container as any)[editingCell.field];
                }

                return (
                    <ContainerEditModal
                        container={container}
                        field={editingCell.field}
                        value={cellValue}
                        isOpen={isModalOpen}
                        onClose={handleCloseModal}
                        onSave={(newValue) => handleSave(editingCell.containerId, editingCell.field, newValue)}
                        isLoading={isUpdating}
                    />
                );
            })()}
        </div>
    );
};
